import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useComms } from './context';
import { usePhone } from './phone';
import {
  peerOfCall,
  DIALER_IDLE,
  type DialerCall,
  type DialerState,
  dialNumber,
  dialerStatusLine,
  reduceWidget,
  startsCall,
  widgetRingFor,
} from './dialerBridge';

/**
 * How long a dial waits for the widget to say a call began before it is
 * called failed. The widget answers a `rc-adapter-new-call` it acts on within
 * a second; six is long enough for a slow web phone and short enough that the
 * agent is still looking at the button.
 */
export const DIAL_WAIT_MS = 6_000;

/**
 * The RingCentral softphone, mounted for the whole module.
 *
 * The frame it drives is the CRM's own `<crm>/dialer` relay page, and it lives
 * at module level rather than inside the Calls tab for one reason: a phone
 * that is not registered BEFORE a call arrives cannot answer it. Mounted per
 * tab, the only calls it could ever take were the ones that happened to ring
 * while the agent was already looking at the call log — which is why the
 * incoming-call card had nothing to offer but a hint.
 *
 * One frame, module-wide. Two would be two web phone registrations for one
 * RingCentral user, and the ring would fork between them. That is also why
 * this hook is one of TWO behind the same interface: inside the dispatch
 * dashboard the shell keeps a frame of its own on every page, and the module
 * mounts none (see useHostDialer).
 *
 * Signing in is the widget's own business: the agent signs it into their own
 * RingCentral user, a session separate from their CRM one, and RingCentral
 * remembers it on its own origin afterwards.
 */
export interface RcDialer {
  /**
   * A RingCentral dialer this agent can use exists at all — here or in the
   * dashboard's shell — AND there is a RingCentral line they may call on.
   * Everything that draws a dialer (the Call route, the New call control, the
   * sign-in bar) reads this and nothing else: a tenant with no RingCentral
   * channel gets no dialer prompts, even when a dashboard shell frames one for
   * every agent regardless.
   */
  available: boolean;
  /**
   * The widget is somebody else's — the dashboard's shell — and this object
   * only remote-controls it. Ring cards and the panel belong to whoever holds
   * the frame, so surfaces that draw one check this first.
   */
  hosted: boolean;
  /**
   * The relay page this module frames itself, or null when it frames none
   * (hosted, or no RingCentral here). Consumed by DialerPanel alone; nothing
   * else needs to know where the widget lives — existence is [available].
   */
  frameUrl: string | null;
  /** Attaches the one frame. A callback ref: the hook holds the node itself. */
  attachFrame: (node: HTMLIFrameElement | null) => void;
  /** The widget has reported its sign-in state at least once. */
  known: boolean;
  /** Signed in AND registered — the two things answering here needs. */
  registered: boolean;
  loggedIn: boolean;
  hasCall: boolean;
  panelOpen: boolean;
  statusLine: string;
  openPanel: () => void;
  closePanel: () => void;
  /** The widget's own session for a ring card, or null when it is not here. */
  ringFor: (sessionKey: string | null, sole: boolean) => DialerCall | null;
  answer: (call: DialerCall) => void;
  ignore: (call: DialerCall) => void;
  /**
   * Place a call on the widget's own phone.
   *
   * Deliberately weaker a requirement than [registered]: answering needs the
   * browser phone because the invite arrives over SIP, but dialling out only
   * needs the widget signed in — it places the call by whatever route the
   * agent has it set to. [canDial] is that condition.
   *
   * Resolves true once the widget itself says a call began, false when it did
   * not within [DIAL_WAIT_MS] — or could not be asked at all. A dial into an
   * iframe is a message, not a call, and a message the widget ignores used to
   * look exactly like one it acted on: a button that did nothing. The caller
   * says so when this is false; the widget's pad has been filled with the
   * number by then so one press there completes it.
   */
  dial: (to: string) => Promise<boolean>;
  canDial: boolean;
  /**
   * The call that is UP in the widget, or null — enough to name it over the
   * thread and open its conversation. The controls stay the widget's.
   */
  activeCall: RcActiveCall | null;
}

export interface RcActiveCall {
  /** RingCentral's telephony session id, when the widget named one. */
  sessionKey: string | null;
  /** The other party's number. */
  peer: string | null;
  /** The other party's name, when the widget matched one. */
  name: string | null;
  /** When the call came up, epoch ms — the timer runs from it. */
  since: number | null;
  /** What the widget's HOLDER knows of the thread; null when this module must look it up. */
  conversationDisplayId: number | null;
}

/** A RingCentral line this agent may call on exists — what makes a dialer worth drawing. */
export const hasRingCentralLine = (
  inboxes: { id: number; channelType: string }[],
  canCall: (inboxId: number) => boolean,
): boolean => inboxes.some((inbox) => inbox.channelType === 'ringcentral' && canCall(inbox.id));

export function useRcDialer(): RcDialer {
  const { source, inboxes, canCall, callsAvailable } = useComms();
  // The Twilio keypad shares this panel's corner. DialerProvider sits inside
  // PhoneProvider, so the dialer can reach the keypad to close it — the keypad
  // cannot reach back, which is why the New call control closes this one.
  const phone = usePhone();
  const hideKeypad = phone?.hidePanel;
  // Loading RingCentral's whole app for a tenant that has no RingCentral — or
  // for an agent with no calling grant anywhere — buys a heavy iframe and
  // nothing else. Both are answered before the frame is ever mounted.
  const url =
    source.dialerUrl && callsAvailable && inboxes.some((inbox) => inbox.channelType === 'ringcentral')
      ? source.dialerUrl
      : null;
  const origin = useMemo(() => (url ? new URL(url, window.location.href).origin : null), [url]);

  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [state, setState] = useState<DialerState>(DIALER_IDLE);
  // The widget's state, mirrored, so one message can be reduced and acted on in
  // the same breath — the panel closing itself is a consequence of the message,
  // not a render that has to happen first.
  const stateRef = useRef<DialerState>(DIALER_IDLE);
  const [panelOpen, setPanelOpen] = useState(false);
  // A panel that opened itself for a call may close itself when that call ends.
  // One the agent opened stays until they close it.
  const openedForCall = useRef(false);
  /** The dial waiting for the widget's word, if any. One at a time. */
  const pendingDial = useRef<{
    number: string;
    resolve: (started: boolean) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);

  const post = useCallback(
    (message: Record<string, unknown>) => {
      const frame = frameRef.current;
      if (!frame?.contentWindow || !origin) return;
      frame.contentWindow.postMessage(message, origin);
    },
    [origin],
  );

  const attachFrame = useCallback((node: HTMLIFrameElement | null) => {
    frameRef.current = node;
  }, []);

  // A call with no in-call controls in sight reads as a button that did
  // nothing — mute, hold, transfer and hang up are all the widget's own. One
  // panel at a time: a keypad left open would paint over the call it came out
  // for.
  const showPanelForCall = useCallback(() => {
    hideKeypad?.();
    setPanelOpen((open) => {
      if (!open) openedForCall.current = true;
      return true;
    });
  }, [hideKeypad]);

  useEffect(() => {
    if (!origin) return;
    const onMessage = (event: MessageEvent) => {
      // Both checks matter: the origin is the relay page's, and the source is
      // the one frame we mounted — the console is itself iframed inside the
      // dashboard, so other windows post here all the time.
      if (event.origin !== origin) return;
      if (event.source !== frameRef.current?.contentWindow) return;
      // Before the reducer's early return: a desk-phone dial (`rc-ringout-call-
      // notify`) changes none of the state the reducer tracks, and it is still
      // the widget saying the dial took.
      if (startsCall(event.data) && pendingDial.current) {
        const pending = pendingDial.current;
        pendingDial.current = null;
        clearTimeout(pending.timer);
        pending.resolve(true);
      }
      const previous = stateRef.current;
      const next = reduceWidget(previous, event.data);
      if (next === previous) return; // the widget chatters constantly
      stateRef.current = next;
      setState(next);
      // A call came up that nobody here pressed for — dialled on the widget's
      // own pad, or answered on it while parked. The controls for it are in the
      // panel, so the panel comes out.
      if (!previous.onCall && next.onCall) showPanelForCall();
      // The call the panel came out for is over — get out of the way again.
      if (openedForCall.current && !next.onCall && next.ringing.length === 0) {
        openedForCall.current = false;
        setPanelOpen(false);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origin, showPanelForCall]);

  // One ring per call: the incoming-call card plays the agent's chosen tone,
  // and the widget's own ringtone would double it.
  useEffect(() => {
    if (state.loggedIn) post({ type: 'rc-adapter-update-ringtone', volume: 0 });
  }, [state.loggedIn, post]);

  const openPanel = useCallback(() => {
    hideKeypad?.();
    openedForCall.current = false;
    setPanelOpen(true);
  }, [hideKeypad]);

  const closePanel = useCallback(() => {
    openedForCall.current = false;
    setPanelOpen(false);
  }, []);

  const ringFor = useCallback(
    (sessionKey: string | null, sole: boolean) => widgetRingFor(state, sessionKey, sole),
    [state],
  );

  const control = useCallback(
    (action: 'answer' | 'reject', call: DialerCall) => {
      post({ type: 'rc-adapter-control-call', callAction: action, callId: call.id });
    },
    [post],
  );

  const answer = useCallback(
    (call: DialerCall) => {
      control('answer', call);
      showPanelForCall();
    },
    [control, showPanelForCall],
  );

  const ignore = useCallback((call: DialerCall) => control('reject', call), [control]);

  /**
   * Click-to-dial. `rc-adapter-new-call` is RingCentral Embeddable's own
   * command and the relay page forwards it like any other `rc-adapter-*`;
   * `toCall` is what PLACES the call, without it the widget only fills its dial
   * pad and waits for a press.
   *
   * The command is then WATCHED: the widget announces a call it places
   * (`startsCall`), and one it does not announce within DIAL_WAIT_MS did not
   * happen — the widget was not signed in after all, is mid-something, or
   * never received the message. Then the pad is filled without `toCall`, so
   * the number is at least in front of the agent, and the promise says false
   * so the button that was pressed can say what happened. A fresh dial
   * supersedes one still waiting.
   */
  const dial = useCallback(
    (to: string) =>
      new Promise<boolean>((resolve) => {
        const number = dialNumber(to);
        if (!number || !stateRef.current.loggedIn) {
          resolve(false);
          return;
        }
        if (pendingDial.current) {
          clearTimeout(pendingDial.current.timer);
          pendingDial.current.resolve(false);
        }
        post({ type: 'rc-adapter-new-call', phoneNumber: number, toCall: true });
        showPanelForCall();
        pendingDial.current = {
          number,
          resolve,
          timer: setTimeout(() => {
            if (pendingDial.current?.number !== number) return;
            pendingDial.current = null;
            post({ type: 'rc-adapter-new-call', phoneNumber: number, toCall: false });
            resolve(false);
          }, DIAL_WAIT_MS),
        };
      }),
    [post, showPanelForCall],
  );

  // A dial left waiting when the frame goes must not hang its caller.
  useEffect(
    () => () => {
      const pending = pendingDial.current;
      if (!pending) return;
      pendingDial.current = null;
      clearTimeout(pending.timer);
      pending.resolve(false);
    },
    [],
  );

  const activeCall = useMemo<RcActiveCall | null>(
    () =>
      state.active
        ? {
            sessionKey: state.active.sessionKey,
            peer: peerOfCall(state.active),
            name: state.active.name,
            since: state.activeSince,
            conversationDisplayId: null,
          }
        : null,
    [state.active, state.activeSince],
  );

  return {
    available: url !== null && hasRingCentralLine(inboxes, canCall),
    hosted: false,
    frameUrl: url,
    attachFrame,
    known: state.known,
    registered: state.loggedIn && state.webphone,
    loggedIn: state.loggedIn,
    hasCall: state.onCall || state.ringing.length > 0,
    panelOpen,
    statusLine: dialerStatusLine(state),
    openPanel,
    closePanel,
    ringFor,
    answer,
    ignore,
    dial,
    canDial: state.loggedIn,
    activeCall,
  };
}
